import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

import {
  deleteGroomerRecurringInventory,
  editSingleInventory,
  getGroomerInventory,
  postGroomerRecurringInventory,
} from 'actions/inventory';
import Button from 'atoms/button';
import toast from 'atoms/toast';
import { finishEditingSingleAvailability } from 'store/reducers/singleAvailability';
import { finishEditingUnavailability } from 'store/reducers/singleUnavailability';
import { closeDrawer } from 'store/reducers/ui';
import { getTimeFromDate } from 'util';

export default function PublishButton() {
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const {
    availability: singleAvailability,
    slotsToDelete,
    isEditingDayAvailability,
  } = useSelector((state) => state.singleAvailability);
  const { recurring, slotsToDelete: recurringToDelete } = useSelector(
    (state) => state.recurringAvailability
  );
  const {
    isEditingUnavailability,
    unavailable: singleUnavailable,
    unavailableToDelete,
  } = useSelector((state) => state.singleUnavailability);
  const { recurring: singleRecurring, slotsToDelete: singleRecurringToDelete } =
    useSelector((state) => state.singleRecurring);

  const dateStart = searchParams.get('start');
  const dateEnd = searchParams.get('end');

  const onClick = async () => {
    if (isEditingDayAvailability) {
      return updateSingleAvailability();
    }
    if (isEditingUnavailability) {
      return updateUnavailability();
    }
    updateRecurringAvailability();
  };

  const updateUnavailability = async () => {
    const cleanUnavailable = singleUnavailable
      .map(({ start, end, isPlaceholder }) => {
        if (isPlaceholder && start && end) {
          return {
            date: {
              start,
              end,
            },
          };
        }
        return null;
      })
      .filter(Boolean);

    const body = {
      unavailable: {
        create: cleanUnavailable,
        delete: unavailableToDelete,
      },
    };

    patchInventory(body);
  };

  const updateSingleAvailability = async () => {
    const slotsToAdd = singleAvailability
      .map(({ start, end, isPlaceholder }) => {
        if (isPlaceholder && start && end) {
          return {
            date: {
              start,
              end,
            },
          };
        }
        return null;
      })
      .filter(Boolean);

    const cleanUnavailable = [...singleUnavailable, ...singleRecurringToDelete]
      .map(({ start, end, isPlaceholder }) => {
        if (isPlaceholder && start && end) {
          return {
            date: {
              start,
              end,
            },
          };
        }
        return null;
      })
      .filter(Boolean);

    const body = {
      single: {
        create: slotsToAdd,
        delete: slotsToDelete,
      },
      unavailable: {
        create: cleanUnavailable,
        delete: unavailableToDelete,
      },
    };
    patchInventory(body);

    const recurringToAdd = singleRecurring.filter((el) => el.isPlaceholder);
    if (recurringToAdd.length > 0) {
      const cleanRecurring = recurringToAdd.map(
        ({ start, end, eventDate }) => ({
          dowId: parseInt(dayjs(new Date(eventDate)).utc().format('d')),
          timeStart: getTimeFromDate(start),
          timeEnd: getTimeFromDate(end),
        })
      );

      dispatch(postGroomerRecurringInventory(cleanRecurring));
    }
  };

  const updateRecurringAvailability = async () => {
    let error = false;

    // 1. POST recurring to delete
    if (recurringToDelete.length > 0) {
      const res = await dispatch(
        deleteGroomerRecurringInventory(recurringToDelete)
      );
      if (!res.success) {
        error = true;
        toast({
          text: "Some availability couldn't be added. Please try again.",
        });
      }
    }

    // 2. POST recurring to add
    const recurringData = [];
    recurring.forEach(({ dow, time }) => {
      const formattedTimes = time.map(({ start, end }) => ({
        dowId: dow.id,
        timeStart: start,
        timeEnd: end,
      }));
      recurringData.push(...formattedTimes);
    });

    const recurringDataToPost = recurringData.filter(
      ({ timeStart, timeEnd }) => timeStart && timeEnd
    );

    await dispatch(postGroomerRecurringInventory(recurringDataToPost)).then(
      ({ success }) => {
        if (!success) {
          error = true;
          toast({
            text: "Some availability couldn't be removed. Please try again.",
          });
        }
      }
    );

    const unavailableToAdd = singleUnavailable
      .map(({ start, end, isPlaceholder }) => {
        if (isPlaceholder && start && end) {
          return {
            date: {
              start,
              end,
            },
          };
        }
        return null;
      })
      .filter(Boolean);

    if (unavailableToAdd.length > 0 || unavailableToDelete.length > 0) {
      const body = {
        unavailable: {
          create: unavailableToAdd,
          delete: unavailableToDelete,
        },
      };

      patchInventory(body);
    } else {
      dispatch(getGroomerInventory({ dateStart, dateEnd }));
      if (!error) {
        toast({ text: 'Your availability has been updated.' });
      }
      dispatch(closeDrawer());
    }
  };

  const patchInventory = (body) => {
    dispatch(editSingleInventory({ body })).then(({ success }) => {
      dispatch(getGroomerInventory({ dateStart, dateEnd }));
      if (success) {
        dispatch(finishEditingSingleAvailability());
        dispatch(finishEditingUnavailability());
        dispatch(closeDrawer());
        toast({ text: 'Your availability has been updated.' });
      }
    });
  };

  return <Button onClick={onClick} text="Save" size="small" />;
}
